<script>
  import { createGroup } from '../../lib/services/groups-api.js';

  let { onClose, onCreated } = $props();

  let name = $state('');
  let description = $state('');
  let submitting = $state(false);
  let error = $state('');

  const MAX_NAME = 50;

  async function handleSubmit(e) {
    e.preventDefault();
    error = '';
    const trimmedName = name.trim();
    if (!trimmedName) {
      error = 'Group name is required.';
      return;
    }
    if (trimmedName.length > MAX_NAME) {
      error = `Name must be ${MAX_NAME} characters or fewer.`;
      return;
    }

    submitting = true;
    try {
      const groupId = await createGroup({ name: trimmedName, description: description.trim() });
      console.log('[CreateGroupModal] Created group:', groupId);
      onCreated(groupId);
    } catch (err) {
      console.error('[CreateGroupModal] Failed to create group:', err);
      error = err.message || 'Failed to create group. Please try again.';
    } finally {
      submitting = false;
    }
  }

  function handleBackdropClick(e) {
    if (e.target === e.currentTarget) onClose();
  }

  function handleKeydown(e) {
    if (e.key === 'Escape') onClose();
  }
</script>

<svelte:window onkeydown={handleKeydown} />

<!-- svelte-ignore a11y_no_static_element_interactions -->
<div class="modal-backdrop" onclick={handleBackdropClick} onkeydown={handleKeydown}>
  <div class="modal" role="dialog" aria-modal="true" aria-labelledby="modal-title">
    <div class="modal-header">
      <h2 id="modal-title" class="modal-title">Create a Group</h2>
      <button class="modal-close" aria-label="Close" onclick={onClose}>
        <svg
          viewBox="0 0 24 24"
          width="20"
          height="20"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </button>
    </div>

    <form onsubmit={handleSubmit} class="modal-form">
      <div class="field">
        <label class="field-label" for="group-name">Group Name *</label>
        <!-- Modal just opened: moving focus into the dialog is the ARIA dialog pattern. -->
        <!-- svelte-ignore a11y_autofocus -->
        <input
          id="group-name"
          class="field-input"
          type="text"
          bind:value={name}
          maxlength={MAX_NAME}
          placeholder="e.g. Thursday Night Trade Crew"
          disabled={submitting}
          autofocus
        />
        <div class="field-hint">{name.trim().length}/{MAX_NAME}</div>
      </div>

      <div class="field">
        <label class="field-label" for="group-desc"
          >Description <span class="field-optional">(optional)</span></label
        >
        <textarea
          id="group-desc"
          class="field-input field-textarea"
          bind:value={description}
          maxlength="300"
          placeholder="What's this group for?"
          rows="3"
          disabled={submitting}></textarea>
      </div>

      {#if error}
        <div class="modal-error">{error}</div>
      {/if}

      <div class="modal-actions">
        <button type="button" class="btn-secondary" onclick={onClose} disabled={submitting}>
          Cancel
        </button>
        <button type="submit" class="btn-primary" disabled={submitting || !name.trim()}>
          {submitting ? 'Creating...' : 'Create Group'}
        </button>
      </div>
    </form>
  </div>
</div>

<style>
  .modal-backdrop {
    position: fixed;
    inset: 0;
    z-index: 500;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
    backdrop-filter: blur(2px);
  }

  .modal {
    background: white;
    border-radius: 16px;
    width: 100%;
    max-width: 440px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  }

  .modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 24px 0;
  }

  .modal-title {
    font-size: 1.125rem;
    font-weight: 700;
    color: #1f2937;
    margin: 0;
  }

  .modal-close {
    width: 32px;
    height: 32px;
    padding: 0;
    background: transparent;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #6b7280;
    transition: background 0.15s;
  }

  .modal-close:hover {
    background: #f3f4f6;
  }

  .modal-form {
    padding: 20px 24px 24px;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .field {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .field-label {
    font-size: 13px;
    font-weight: 600;
    color: #374151;
  }

  .field-optional {
    font-weight: 400;
    color: #9ca3af;
  }

  .field-input {
    padding: 10px 12px;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 14px;
    color: #1f2937;
    background: white;
    font-family: inherit;
    transition:
      border-color 0.15s,
      box-shadow 0.15s;
    outline: none;
  }

  .field-input:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
  }

  .field-input:disabled {
    background: #f9fafb;
    color: #9ca3af;
  }

  .field-textarea {
    resize: vertical;
    min-height: 80px;
  }

  .field-hint {
    font-size: 11px;
    color: #9ca3af;
    text-align: right;
    margin-top: -2px;
  }

  .modal-error {
    font-size: 13px;
    color: #ef4444;
    background: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 6px;
    padding: 10px 12px;
  }

  .modal-actions {
    display: flex;
    gap: 8px;
    justify-content: flex-end;
    margin-top: 4px;
  }

  .btn-secondary {
    padding: 9px 18px;
    background: transparent;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    color: #374151;
    cursor: pointer;
    transition: background 0.15s;
    font-family: inherit;
  }

  .btn-secondary:hover:not(:disabled) {
    background: #f3f4f6;
  }

  .btn-primary {
    padding: 9px 18px;
    background: #3b82f6;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    color: white;
    cursor: pointer;
    transition:
      background 0.15s,
      opacity 0.15s;
    font-family: inherit;
  }

  .btn-primary:hover:not(:disabled) {
    background: #2563eb;
  }

  .btn-primary:disabled,
  .btn-secondary:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
</style>
